/**
 * Section-hub configuration (Phase-2 nav).
 *
 * Some rail sections are best presented as a Canva-style "hub" — a landing page of
 * cards/links in the content pane — rather than (or behind) a secondary drawer. This is
 * the same pattern as the artist dashboard launcher. The config lives here as the SINGLE
 * source of truth so both the route definitions (app-routing.module.ts, via route data)
 * and the nav shell (ds-shell, for rail link + active-state matching) stay in sync.
 *
 * A card link segment of ':me' is resolved to the current artist's username at render
 * time by DsSectionHubComponent (used for the public-profile link).
 */
export interface HubCard {
    label: string;
    description: string;
    icon: string;
    link: any[];
    accent: 'teal' | 'orange';
}

export interface SectionHub {
    /** Route path segment (e.g. 'selling') and its router-link form. */
    path: string;
    link: any[];
    title: string;
    subtitle: string;
    icon: string;
    cards: HubCard[];
}

export const SELLING_HUB: SectionHub = {
    path: 'selling',
    link: ['/selling'],
    title: 'Selling',
    subtitle: 'Place work with galleries, sync your stores, and print what you need.',
    icon: 'fa fa-tag',
    cards: [
        { label: 'Consignments', description: "Track the work you've placed with galleries.", icon: 'fa fa-handshake-o', link: ['/view-consignments'], accent: 'teal' },
        { label: 'Add a consignment', description: 'Send a set of pieces to a gallery.', icon: 'fa fa-plus', link: ['/artists', 'add-consignment'], accent: 'teal' },
        { label: 'Sync products', description: 'Push your artworks out to your sales channels.', icon: 'fa fa-refresh', link: ['/sync-products'], accent: 'teal' },
        { label: 'Labels', description: 'Generate price and information labels.', icon: 'fa fa-tags', link: ['/labels-generation'], accent: 'orange' },
        { label: 'Connect Shopify', description: 'Sell your work through your Shopify store.', icon: 'fa fa-shopping-bag', link: ['/connect-with-shopify-account'], accent: 'orange' },
        { label: 'Connect Squarespace', description: 'Sell your work through Squarespace.', icon: 'fa fa-square-o', link: ['/connect-with-squarespace-account'], accent: 'orange' },
        { label: 'Connect WooCommerce', description: 'Sell your work through WooCommerce.', icon: 'fa fa-shopping-cart', link: ['/connect-with-woocommerce-account'], accent: 'orange' },
    ],
};

export const WEBSITE_HUB: SectionHub = {
    path: 'website',
    link: ['/website'],
    title: 'Your website',
    subtitle: 'Build your ArtFlow site, share your portfolio, and create links for the platforms you already use.',
    icon: 'fa fa-globe',
    cards: [
        { label: 'ArtFlow website builder', description: 'Build and publish your free ArtFlow portfolio website.', icon: 'fa fa-paint-brush', link: ['/artists', 'portfolio'], accent: 'teal' },
        { label: 'View your public portfolio', description: 'See your live portfolio the way galleries and collectors do.', icon: 'fa fa-external-link', link: ['/profile', ':me'], accent: 'teal' },
        { label: 'Portfolio links for WordPress, Wix & Squarespace', description: 'Create portfolio links to embed in the website platform you already use.', icon: 'fa fa-code', link: ['/artists', 'portfolio'], accent: 'orange' },
    ],
};

export const SOCIAL_ACCOUNTS_HUB: SectionHub = {
    path: 'social-accounts',
    link: ['/social-accounts'],
    title: 'Your social accounts',
    subtitle: 'See and manage the social accounts connected to ArtFlow.',
    icon: 'fa fa-share-alt',
    cards: [
        { label: 'Social media shares', description: 'Share your work across your connected channels.', icon: 'fa fa-share-alt', link: ['/social-media'], accent: 'teal' },
        { label: 'Facebook accounts', description: 'Manage your connected Facebook pages.', icon: 'fa fa-facebook', link: ['/facebook-accounts'], accent: 'orange' },
        { label: 'Instagram accounts', description: 'Manage your connected Instagram accounts.', icon: 'fa fa-instagram', link: ['/instagram-account'], accent: 'orange' },
    ],
};

export const LINK_SOCIALS_HUB: SectionHub = {
    path: 'link-socials',
    link: ['/link-socials'],
    title: 'Link your socials',
    subtitle: 'Connect your social accounts so you can share your work from ArtFlow.',
    icon: 'fa fa-link',
    cards: [
        { label: 'Connect Facebook', description: 'Link a Facebook page to ArtFlow.', icon: 'fa fa-facebook-square', link: ['/connect-with-facebook'], accent: 'teal' },
        { label: 'Connect Instagram', description: 'Link an Instagram account to ArtFlow.', icon: 'fa fa-instagram', link: ['/connect-with-instagram'], accent: 'teal' },
    ],
};

// ---- Gallery hubs ----

export const GALLERY_SELLING_HUB: SectionHub = {
    path: 'gallery-selling',
    link: ['/gallery-selling'],
    title: 'Selling',
    subtitle: 'Manage consignments and keep your sales channels in sync.',
    icon: 'fa fa-tag',
    cards: [
        { label: 'Consignments', description: 'Review and manage consignments with your artists.', icon: 'fa fa-handshake-o', link: ['/view-consignments'], accent: 'teal' },
        { label: 'Product sync', description: 'Push gallery products out to your sales channels.', icon: 'fa fa-refresh', link: ['/product-sync'], accent: 'teal' },
        { label: 'Connect Shopify', description: 'Sell through your Shopify store.', icon: 'fa fa-shopping-bag', link: ['/connect-with-shopify-account'], accent: 'orange' },
        { label: 'Connect Squarespace', description: 'Sell through Squarespace.', icon: 'fa fa-square-o', link: ['/connect-with-squarespace-account'], accent: 'orange' },
        { label: 'Connect WooCommerce', description: 'Sell through WooCommerce.', icon: 'fa fa-shopping-cart', link: ['/connect-with-woocommerce-account'], accent: 'orange' },
        { label: 'Connect Square POS', description: 'Sell through Square Point of Sale.', icon: 'fa fa-credit-card', link: ['/connect-squareup-pos-account'], accent: 'orange' },
        { label: 'Connect Wix', description: 'Sell through your Wix site.', icon: 'fa fa-globe', link: ['/connect-with-wix-account'], accent: 'orange' },
    ],
};

export const GALLERY_WEBSITE_HUB: SectionHub = {
    path: 'gallery-website',
    link: ['/gallery-website'],
    title: 'Your website',
    subtitle: 'Publish your gallery portfolio and share collections.',
    icon: 'fa fa-globe',
    cards: [
        { label: 'Gallery portfolio', description: 'Build and publish your gallery portfolio.', icon: 'fa fa-paint-brush', link: ['/galleries', 'portfolio'], accent: 'teal' },
        { label: 'Shareable products', description: 'Create shareable links for selected products.', icon: 'fa fa-share-alt', link: ['/galleries', 'gallery-shareable-product'], accent: 'teal' },
    ],
};

/** All route-path prefixes a hub "owns", for rail active-state matching. */
export function hubMatchPrefixes(hub: SectionHub): string[] {
    const toPath = (l: any[]) => '/' + l.filter(s => typeof s === 'string').join('/');
    return [toPath(hub.link), ...hub.cards.map(c => toPath(c.link))];
}
